import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GraduationCap, Loader2, Download, CheckCircle, XCircle, AlertTriangle, FolderArchive } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const STATUTS = {
  eligible: { label: 'Éligible', cls: 'bg-green-100 text-green-700' },
  non_eligible: { label: 'Non éligible', cls: 'bg-red-100 text-red-700' },
  deja_moniteur: { label: 'Déjà moniteur', cls: 'bg-purple-100 text-purple-700' },
  introuvable: { label: 'Introuvable', cls: 'bg-slate-100 text-slate-600' },
};

const EligibiliteList = ({ items }) => (
  <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="moniteurs-lot-eligibilite">
    {items.map((it) => (
      <div key={it.user_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`moniteurs-lot-row-${it.user_id}`}>
        <span className="font-medium text-[#0F172A] truncate">{it.prenom} {it.nom}</span>
        <span className="flex items-center gap-2 shrink-0">
          {it.bc_manquants?.length > 0 && <span className="text-xs text-red-600">{it.bc_manquants.join(', ')} non validé{it.bc_manquants.length > 1 ? 's' : ''}</span>}
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUTS[it.statut]?.cls}`}>{STATUTS[it.statut]?.label}</span>
        </span>
      </div>
    ))}
  </div>
);

const ResultatsList = ({ resultats }) => (
  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="moniteurs-lot-resultats">
    {resultats.map((r) => (
      <div key={r.user_id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
        <span className="font-medium text-[#0F172A] truncate">{r.prenom} {r.nom}</span>
        <span className={`flex items-center gap-1 text-xs ${r.ok ? 'text-green-700' : 'text-red-600'}`}>
          {r.ok ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {r.message}
        </span>
      </div>
    ))}
  </div>
);

export const MoniteursLotDialog = ({ open, onOpenChange, selectedIds, onDone }) => {
  const [items, setItems] = useState(null);
  const [job, setJob] = useState(null);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resultats, setResultats] = useState(null);
  const timer = useRef(null);

  const stopPolling = () => { if (timer.current) clearInterval(timer.current); timer.current = null; };

  useEffect(() => {
    if (!open) return undefined;
    setItems(null); setJob(null); setDownloaded(false); setConfirmed(false); setResultats(null);
    axios.post('/moniteur/lot/eligibilite', { user_ids: selectedIds })
      .then(({ data }) => setItems(data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Impossible de vérifier l'éligibilité"); onOpenChange(false); });
    return stopPolling;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const eligibles = (items || []).filter(i => i.statut === 'eligible');
  const archivedIds = job?.archived_ids || [];

  const preparer = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('/dossiers-lot', { user_ids: eligibles.map(i => i.user_id), source: 'fin_formation' });
      setJob(data);
      timer.current = setInterval(async () => {
        try {
          const res = await axios.get(`/dossiers-lot/${data.job_id}`);
          setJob(res.data);
          if (res.data.status !== 'en_cours') stopPolling();
        } catch (e) { stopPolling(); }
      }, 2000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de préparer les archives');
    } finally {
      setBusy(false);
    }
  };

  const telecharger = () => {
    const link = document.createElement('a');
    link.href = `${axios.defaults.baseURL || ''}/dossiers-lot/${job.job_id}/download?token=${job.download_token}`;
    link.setAttribute('download', job.filename || 'Dossiers_fin_formation.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
    setDownloaded(true);
  };

  const convertir = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post('/moniteur/lot/convertir', { job_id: job.job_id, user_ids: archivedIds });
      const traites = new Set(data.resultats.map(r => r.user_id));
      const nonInclus = items.filter(i => !traites.has(i.user_id)).map(i => ({
        ...i,
        ok: false,
        message: `Non inclus : ${STATUTS[i.statut]?.label || 'archive non générée'}${i.bc_manquants?.length ? ` (${i.bc_manquants.join(', ')} manquant${i.bc_manquants.length > 1 ? 's' : ''})` : ''}`,
      }));
      setResultats([...data.resultats, ...nonInclus]);
      toast.success(`${data.convertis} stagiaire(s) converti(s) en Moniteur VNM`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-xl" data-testid="moniteurs-lot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-purple-600" /> Passer en Moniteur VNM</DialogTitle>
          <DialogDescription>Seuls les stagiaires ayant validé les 3 BC peuvent être convertis. Action irréversible.</DialogDescription>
        </DialogHeader>

        {!items ? (
          <div className="flex items-center justify-center py-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Vérification…</div>
        ) : resultats ? (
          <ResultatsList resultats={resultats} />
        ) : (
          <div className="space-y-4">
            <EligibiliteList items={items} />
            <p className="text-sm text-slate-600" data-testid="moniteurs-lot-eligibles-count">{eligibles.length} stagiaire(s) éligible(s) sur {items.length}</p>

            {eligibles.length > 0 && !job && (
              <Button onClick={preparer} disabled={busy} className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="moniteurs-lot-prepare-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
                Étape 1 : préparer les archives de fin de formation
              </Button>
            )}

            {job?.status === 'en_cours' && (
              <div data-testid="moniteurs-lot-progress">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-[#8DC63F]" /> {job.done} / {job.total} archive(s)…</span>
                  <span className="text-slate-500 truncate ml-2">{job.current}</span>
                </div>
                <Progress value={job.total ? Math.round((job.done / job.total) * 100) : 0} className="h-2 bg-slate-200 [&>div]:bg-[#8DC63F]" />
              </div>
            )}

            {job && ['erreur', 'interrompu', 'expire'].includes(job.status) && (
              <p className="flex items-center gap-2 text-sm text-amber-700" data-testid="moniteurs-lot-error"><AlertTriangle className="h-4 w-4" /> La préparation n'a pas abouti{job.error ? ` : ${job.error}` : ''}. Fermez et relancez.</p>
            )}

            {job?.status === 'pret' && (
              <div className="space-y-3">
                <Button onClick={telecharger} className="w-full bg-[#8DC63F] hover:bg-[#7AB62F] text-white" data-testid="moniteurs-lot-download-btn">
                  <Download className="h-4 w-4 mr-2" /> Étape 2 : télécharger l'archive ({archivedIds.length} stagiaire(s))
                </Button>
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="mt-0.5 h-4 w-4 accent-purple-600" checked={confirmed} disabled={!downloaded} onChange={(e) => setConfirmed(e.target.checked)} data-testid="moniteurs-lot-confirm-checkbox" />
                  J'ai téléchargé et vérifié l'archive. Je confirme le passage en Moniteur VNM (irréversible).
                </label>
                <Button onClick={convertir} disabled={!downloaded || !confirmed || busy || archivedIds.length === 0} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="moniteurs-lot-convert-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GraduationCap className="h-4 w-4 mr-2" />}
                  Étape 3 : convertir {archivedIds.length} stagiaire(s)
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MoniteursLotDialog;
